// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { browserTimeZone, canonicalZone, DEFAULT_TIME_ZONE, effectiveTimeZone, ELogTimeZonePolicy, formatDateTimeZone, formatMyTime, primaryTimeZone, } from "@sequentech/ui-core";
export { DEFAULT_TIME_ZONE, effectiveTimeZone, primaryTimeZone };
/** A date from an ISO string, a `Date` or Unix milliseconds; null when it isn't one. */
export var toDate = function (value) {
    if (value === null || value === undefined || value === "")
        return null;
    var date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};
/**
 * The zone the Logs tab shows a row of `election` in: the primary under the
 * PRIMARY policy, else the row's election zone (sequent-core `log_time_zone`).
 */
export var logTimeZone = function (event, election) {
    var _a, _b;
    return ((_b = (_a = event === null || event === void 0 ? void 0 : event.timezones) === null || _a === void 0 ? void 0 : _a.logs) !== null && _b !== void 0 ? _b : ELogTimeZonePolicy.ELECTION) === ELogTimeZonePolicy.PRIMARY
        ? primaryTimeZone(event)
        : effectiveTimeZone(event, election);
};
/**
 * The date and time part in `zone`, in the i18n language's format: the
 * timezone service's default (medium date, short time), with seconds for
 * logs and tasks.
 */
export var formatWallTime = function (date, zone, lang, seconds) {
    if (seconds === void 0) { seconds = false; }
    var locale = intlLocale(lang);
    var options = {
        dateStyle: "medium",
        timeStyle: seconds ? "medium" : "short",
    };
    try {
        return new Intl.DateTimeFormat(locale, __assign(__assign({}, options), { timeZone: zone })).format(date);
    }
    catch (_a) {
        // A zone name the browser doesn't know: show it in UTC rather than
        // in the browser's zone, as the server does.
        return new Intl.DateTimeFormat(locale, __assign(__assign({}, options), { timeZone: DEFAULT_TIME_ZONE })).format(date);
    }
};
/** The Intl locale of an i18n language: Catalan is "cat" in the portals. */
var intlLocale = function (lang) {
    var _a;
    var locale = lang.split("-")[0] === "cat" ? "ca" : lang;
    try {
        return (_a = Intl.getCanonicalLocales(locale)[0]) !== null && _a !== void 0 ? _a : "en";
    }
    catch (_b) {
        return "en";
    }
};
/** "Apr 9, 2028, 3:41:15 AM PhST" (timezones.dateTimeZone). */
export var formatZoned = function (value, zone, _a) {
    var t = _a.t, lang = _a.lang, seconds = _a.seconds;
    var date = toDate(value);
    if (!date)
        return "";
    return formatDateTimeZone(date, zone, {
        t: t,
        lang: lang,
        formatDateTime: function (instant, inZone) { return formatWallTime(instant, inZone, lang, seconds); },
    });
};
/** The same zone, so a "my time" line would repeat the first. */
export var sameZone = function (left, right) {
    return canonicalZone(left) === canonicalZone(right);
};
/**
 * "Apr 8, 2028, 6:00:03 PM EDT · my time" (timezones.myTime), in the
 * browser's zone; null when the value is already shown in it.
 */
export var formatMine = function (value, zone, _a, myZone) {
    var t = _a.t, lang = _a.lang, seconds = _a.seconds;
    if (myZone === void 0) { myZone = browserTimeZone(); }
    var date = toDate(value);
    if (!date || sameZone(zone, myZone))
        return null;
    return formatMyTime(date, {
        t: t,
        lang: lang,
        formatDateTime: function (instant, inZone) { return formatWallTime(instant, inZone, lang, seconds); },
    }, myZone);
};
